import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import FileTypeIcon from './FileTypeIcon';

const DeleteDocumentDialog = ({
  open,
  onClose,
  documents = [],
  onConfirm,
  loading = false
}) => {
  const isBulkDelete = documents.length > 1;
  const singleDoc = documents[0];

  const cleanFileName = (key) => {
    return key ? key.replace(/^tenants\/[^/]+\/uploads\//, '') : '';
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <WarningIcon color="error" />
        <Typography variant="h6" component="div">
          {isBulkDelete 
            ? `Delete ${documents.length} Documents` 
            : 'Delete Document'
          }
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            This action cannot be undone. {isBulkDelete ? 'These documents' : 'This document'} will be permanently removed from the S3 bucket.
          </Typography>
        </Alert>

        {isBulkDelete ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              You are about to delete the following {documents.length} documents:
            </Typography>
            <List 
              dense 
              sx={{ 
                maxHeight: 200, 
                overflow: 'auto',
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              {documents.map((doc, index) => (
                <ListItem key={doc.key || index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <FileTypeIcon fileName={cleanFileName(doc.key)} size="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap>
                        {cleanFileName(doc.key)}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(doc.size)}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          singleDoc && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete this document?
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}
              >
                <FileTypeIcon fileName={cleanFileName(singleDoc.key)} size="large" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={500}>
                    {cleanFileName(singleDoc.key)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Size: {formatFileSize(singleDoc.size)}
                  </Typography>
                  {singleDoc.lastModified && (
                    <Typography variant="caption" color="text.secondary">
                      Modified: {new Date(singleDoc.lastModified).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )
        )}

        <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 500 }}>
          ⚠️ This action is irreversible!
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
          disabled={loading}
          sx={{
            minWidth: 120,
            '&:hover': {
              bgcolor: 'error.dark'
            }
          }}
        >
          {loading ? 'Deleting...' : `Delete ${isBulkDelete ? 'All' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteDocumentDialog;